import { Plus, Save } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import BaseDataTable, {
  ColumnType,
  IDataRow,
} from "../../../../components/ui/baseDataTable";
import ButtonV2 from "../../../../components/ui/button/v2";
import { showErrorToast, showSuccessToast } from "../../../../lib/toast";
import { cn } from "../../../../lib/utils";
import {
  fetchPartyOptionsThunk,
  upsertPartiesInvolvedThunk,
} from "../../../partiesinvolved/logic/PartiesInvolvedActionsThunk";
import {
  selectPartiesWithRecommendedAction,
  selectPartyOptions,
} from "../../../partiesinvolved/logic/PartiesInvolvedSelectors";
import {
  clearPartyOptions,
  resetDeleteStatus,
  resetUpsertStatus,
  type IPartyOptions,
} from "../../../partiesinvolved/logic/PartiesInvolvedSlice";
import { RECOMMENDED_ACTION_DROPDOWN_OPTIONS } from "../../constants/dropdown";
import AddNewPartyModal from "./AddNewPartyModal";
import ClosureNoteModal from "./ClosureNoteModal";
import NoDataIcon from "../../../../assets/svg/EmptyTableIcon";
import { getCollaborators } from "../../../../../src/features/users/logic/slice";

interface IActionsAgainstInvolvedPartyData {
  id: string;
  partyInvolved: string;
  recommendedActions: string[];
  implementationOwner: string;
  approvedBy: string;
  implementationDate: string;
  closureNote: string;
}

interface IActionsAgainstInvolvedPartiesTableProps {
  caseId: string;
}

const ActionsAgainstInvolvedPartiesTable: React.FC<
  IActionsAgainstInvolvedPartiesTableProps
> = (props) => {
  const dispatch = useAppDispatch();
  const syntheticPartyIdByKeyRef = useRef<Record<string, string>>({});
  const initialRowOrderRef = useRef<string[]>([]);

  const collaboratorsData = useAppSelector(getCollaborators);
  const partyOptionsData = useAppSelector(selectPartyOptions) as
    | IPartyOptions[]
    | undefined;
  const partiesWithRecommendedAction = useAppSelector(
    selectPartiesWithRecommendedAction
  );

  const [partiesInvolvedEdited, setPartiesInvolvedEdited] = useState<
    Map<string, IActionsAgainstInvolvedPartyData>
  >(new Map());
  const [partiesInvolvedAdded, setPartiesInvolvedAdded] = useState<
    IActionsAgainstInvolvedPartyData[]
  >([]);
  const [createdPartyOptions, setCreatedPartyOptions] = useState<
    { value: string; name: string; subTitle?: string }[]
  >([]);

  const [isClosureNoteModalOpen, setIsClosureNoteModalOpen] = useState(false);
  const [isAddPartyModalOpen, setIsAddPartyModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [modalVariant, setModalVariant] = useState<"edit" | "view">("edit");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getStablePartyOptionValue = (party: {
    party_id?: string | null;
    party_name?: string | null;
    party_type?: string | null;
  }) => {
    if (party.party_id) return party.party_id;
    const key = `${party.party_name ?? ""}::${party.party_type ?? ""}`;
    if (!syntheticPartyIdByKeyRef.current[key]) {
      syntheticPartyIdByKeyRef.current[key] = crypto.randomUUID();
    }
    return syntheticPartyIdByKeyRef.current[key];
  };

  useEffect(() => {
    dispatch(fetchPartyOptionsThunk(props.caseId));
  }, [props.caseId, dispatch]);

  useEffect(() => {
    if (
      initialRowOrderRef.current.length === 0 &&
      partiesWithRecommendedAction?.length
    ) {
      initialRowOrderRef.current = partiesWithRecommendedAction.map(
        (p) => p.party_id
      );
    }
  }, [partiesWithRecommendedAction]);

  useEffect(() => {
    return () => {
      syntheticPartyIdByKeyRef.current = {};
      dispatch(clearPartyOptions());
    };
  }, [dispatch]);

  const tableData: IActionsAgainstInvolvedPartyData[] = React.useMemo(() => {
    const rowMap = new Map<string, IActionsAgainstInvolvedPartyData>();

    (partiesWithRecommendedAction || []).forEach((party) => {
      rowMap.set(party.party_id, {
        id: party.party_id,
        partyInvolved: party.party_id,
        recommendedActions: party.recommended_actions || [],
        implementationOwner: party.implementation_owner || "",
        approvedBy: party.recommended_action_approver || "",
        implementationDate: party.recommended_action_implementation_date || "",
        closureNote: party.closure_note || "",
      });
    });

    partiesInvolvedEdited.forEach((editedRow, id) => {
      rowMap.set(id, editedRow);
    });

    let orderedRows: IActionsAgainstInvolvedPartyData[];

    if (initialRowOrderRef.current.length === 0) {
      orderedRows = Array.from(rowMap.values());
    } else {
      orderedRows = initialRowOrderRef.current
        .map((id) => rowMap.get(id))
        .filter(Boolean) as IActionsAgainstInvolvedPartyData[];
    }

    if (initialRowOrderRef.current.length > 0) {
      const orderedIds = new Set(orderedRows.map((r) => r.id));
      const missing = Array.from(rowMap.values()).filter(
        (row) => !orderedIds.has(row.id)
      );
      if (missing.length > 0) {
        orderedRows = [...orderedRows, ...missing];
      }
    }

    return [...orderedRows, ...partiesInvolvedAdded];
  }, [
    partiesWithRecommendedAction,
    partiesInvolvedEdited,
    partiesInvolvedAdded,
  ]);

  const partyMetaByValue = React.useMemo(() => {
    const map = new Map<
      string,
      { party_id?: string; name: string; party_type?: string }
    >();

    createdPartyOptions.forEach((opt) => {
      map.set(opt.value, {
        party_id: opt.value,
        name: opt.name,
        party_type: opt.subTitle,
      });
    });

    (partiesWithRecommendedAction || []).forEach((party) => {
      map.set(party.party_id, {
        party_id: party.party_id,
        name: party.name,
        party_type: party.party_type ?? undefined,
      });
    });

    (partyOptionsData || []).forEach((party: IPartyOptions) => {
      const value = getStablePartyOptionValue(party);
      map.set(value, {
        party_id: party.party_id ?? undefined,
        name: party.party_name,
        party_type: party.party_type ?? undefined,
      });
    });

    return map;
  }, [partyOptionsData, createdPartyOptions, partiesWithRecommendedAction]);

  const partyOptions = React.useMemo(
    () =>
      Array.from(partyMetaByValue.entries()).map(([value, meta]) => ({
        name: meta.name,
        subTitle: meta.party_type,
        value,
        disabled: !!meta.party_id
          ? (partiesWithRecommendedAction || []).some(
              (p) => p.party_id === meta.party_id
            )
          : false,
      })),
    [partyMetaByValue, partiesWithRecommendedAction]
  );

  const implementationOwnerOptions = collaboratorsData.map((collaborator) => ({
    name: `${collaborator.user_name} - ${collaborator.department}`,
    value: collaborator.user_name,
  }));

  const updatePartyData = (
    rowIndex: number,
    updates: Partial<IActionsAgainstInvolvedPartyData>
  ) => {
    const currentRow = tableData[rowIndex];
    if (!currentRow) return;

    const partyId = currentRow.id;
    const updatedRow = { ...currentRow, ...updates };

    const addedIndex = partiesInvolvedAdded.findIndex(
      (party) => party.id === partyId
    );

    if (addedIndex !== -1) {
      setPartiesInvolvedAdded((prev) => {
        const newAdded = [...prev];
        newAdded[addedIndex] = updatedRow;
        return newAdded;
      });
    } else {
      setPartiesInvolvedEdited((prev) => {
        const newEdited = new Map(prev);
        newEdited.set(partyId, updatedRow);
        return newEdited;
      });
    }
  };

  const handleClosureNoteClick = (rowIndex: number) => {
    const row = tableData[rowIndex];
    const hasNote = row?.closureNote && row.closureNote?.length > 0;

    setSelectedRowIndex(rowIndex);
    setModalVariant(hasNote ? "view" : "edit");
    setIsClosureNoteModalOpen(true);
  };

  const handleSaveClosureNote = (note: string) => {
    if (selectedRowIndex !== null) {
      updatePartyData(selectedRowIndex, { closureNote: note });
    }
  };

  const handleCloseClosureNoteModal = () => {
    setIsClosureNoteModalOpen(false);
    setSelectedRowIndex(null);
  };

  const handleOpenAddPartyModal = () => {
    setIsAddPartyModalOpen(true);
  };

  const handleCloseAddPartyModal = () => {
    setIsAddPartyModalOpen(false);
  };

  const handleSaveParty = (partyName: string, partyType: string) => {
    const matchingPartyOption = partyOptionsData?.find(
      (option: IPartyOptions) =>
        option.party_name === partyName && option.party_type === partyType
    );

    const valueToUse = matchingPartyOption
      ? getStablePartyOptionValue(matchingPartyOption)
      : crypto.randomUUID();

    if (!matchingPartyOption) {
      setCreatedPartyOptions((prev) => [
        ...prev,
        { value: valueToUse, name: partyName, subTitle: partyType },
      ]);
    }

    const newParty: IActionsAgainstInvolvedPartyData = {
      id: crypto.randomUUID(),
      partyInvolved: valueToUse,
      recommendedActions: [],
      implementationOwner: "",
      approvedBy: "",
      implementationDate: "",
      closureNote: "",
    };

    setPartiesInvolvedAdded((prev) =>
      partiesInvolvedAdded.length > 0 ? [newParty] : [...prev, newParty]
    );

    handleCloseAddPartyModal();
  };

  const handleAddTableRowClick = () => {
    if (partiesInvolvedAdded.length > 0) {
      return;
    }

    setPartiesInvolvedAdded((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        partyInvolved: "",
        recommendedActions: [],
        implementationOwner: "",
        approvedBy: "",
        implementationDate: "",
        closureNote: "",
      },
    ]);
  };

  const handleTableChange = <T extends keyof IActionsAgainstInvolvedPartyData>(
    rowIndex: number,
    value: IActionsAgainstInvolvedPartyData[T],
    field: T
  ) => {
    const currentRow = tableData[rowIndex];
    if (!currentRow) return;

    const updatedRow = { ...currentRow, [field]: value };

    const addedIndex = partiesInvolvedAdded.findIndex(
      (party) => party.id === currentRow.id
    );

    if (addedIndex !== -1) {
      setPartiesInvolvedAdded((prev) => {
        const newAdded = [...prev];
        newAdded[addedIndex] = updatedRow;
        return newAdded;
      });
    } else {
      setPartiesInvolvedEdited((prev) => {
        const newEdited = new Map(prev);
        newEdited.set(currentRow.id, updatedRow);
        return newEdited;
      });
    }
  };

  const renderAddPartyFooter = () => (
    <div className="sticky bottom-0 bg-white z-10 flex w-full flex-col justify-center items-end border-t border-solid border-[#E5E5E5] p-3">
      <button
        className="flex items-center gap-1.5 text-[#3551F3] font-sans font-medium text-sm leading-[100%] hover:opacity-80 transition-opacity cursor-pointer"
        onClick={handleOpenAddPartyModal}
      >
        <Plus className="w-4 h-4" />
        <span>Add New</span>
      </button>
    </div>
  );

  const columnConfigs = [
    {
      headerName: "Party Involved",
      field: "partyInvolved" as keyof IActionsAgainstInvolvedPartyData,
      type: "select" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) => data.partyInvolved,
      isEditable: (
        _rowIndex: number,
        rowData: IActionsAgainstInvolvedPartyData
      ) => partiesInvolvedAdded.some((p) => p.id === rowData.id),
      onChange: handleTableChange,
      options: partyOptions,
      selectCellProps: {
        placeholder: "Add",
        showTitleSubTitle: true,
        titleClassName: "max-w-[190px]",
        showTooltip: true,
        footer: renderAddPartyFooter(),
        allowOpenWhenReadOnly: true,
        hideDisabledOptions: true,
      },
    },
    {
      headerName: "Recommended Actions",
      field: "recommendedActions" as keyof IActionsAgainstInvolvedPartyData,
      type: "multiselect" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) =>
        data.recommendedActions,
      isEditable: true,
      onChange: handleTableChange,
      options: RECOMMENDED_ACTION_DROPDOWN_OPTIONS,
      multiselectCellProps: {
        name: "Recommended Actions",
        rootContainerClassName: "min-w-[160px]",
        dropdownClassName: "min-w-[250px] min-h-[380px]",
      },
    },
    {
      headerName: "Implementation Owner",
      field: "implementationOwner" as keyof IActionsAgainstInvolvedPartyData,
      type: "select" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) =>
        data.implementationOwner,
      isEditable: true,
      onChange: handleTableChange,
      options: implementationOwnerOptions,
      selectCellProps: {
        placeholder: "Add",
        showTooltip: true,
        showTitleSubTitle: true,
        dropdownClassName: "w-max min-w-full",
        titleClassName:
          " font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
      },
    },
    {
      headerName: "Approved By",
      field: "approvedBy" as keyof IActionsAgainstInvolvedPartyData,
      type: "text" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) => data.approvedBy,
      isEditable: true,
      onChange: handleTableChange,
      textCellProps: {
        textareaClassName: "max-w-[197px]",
        numberOfRows: 1,
      },
    },
    {
      headerName: "Implementation Date",
      field: "implementationDate" as keyof IActionsAgainstInvolvedPartyData,
      type: "date" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) =>
        data.implementationDate,
      isEditable: true,
      onChange: handleTableChange,
    },
    {
      headerName: "Closure Note",
      field: "closureNote" as keyof IActionsAgainstInvolvedPartyData,
      type: "textbutton" as ColumnType,
      getValue: (data: IActionsAgainstInvolvedPartyData) => {
        const hasNote = data?.closureNote && data?.closureNote?.length > 0;
        return hasNote ? "View notes" : "+ Add closure note";
      },
      onClick: handleClosureNoteClick,
    },
  ];

  const handleDeleteRow = async (rowIndex: number) => {
    const rowToDelete = tableData[rowIndex];
    const partyId = rowToDelete.id;

    const addedIndex = partiesInvolvedAdded.findIndex(
      (party) => party.id === partyId
    );

    if (addedIndex !== -1) {
      setPartiesInvolvedAdded((prev) =>
        prev.filter((_, i) => i !== addedIndex)
      );
    } else {
      try {
        setIsDeleting(true);
        const row = tableData[rowIndex];
        const meta = partyMetaByValue.get(row.partyInvolved);

        if (!meta?.party_id) {
          throw new Error("Party ID not found for upsert delete");
        }

        const payload = {
          party_id: meta.party_id,
          name: meta.name,
          party_type: meta.party_type ?? null,
          recommended_actions: [],
          recommended_action_approver: null,
          recommended_action_implementation_date: null,
          implementation_owner: null,
          closure_note: null,
        };

        await dispatch(
          upsertPartiesInvolvedThunk({
            case_id: props.caseId,
            request: {
              parties_involved: [payload],
            },
          })
        ).unwrap();

        showSuccessToast("Party deleted successfully!");
      } catch (error: any) {
        showErrorToast("Failed to delete party");
      } finally {
        setIsDeleting(false);
        dispatch(resetDeleteStatus());
      }
    }
  };

  const handleDiscardChangesClick = () => {
    setPartiesInvolvedEdited(new Map());
    setPartiesInvolvedAdded([]);
    setCreatedPartyOptions([]);
    setIsClosureNoteModalOpen(false);
    setIsAddPartyModalOpen(false);
    setSelectedRowIndex(null);
    setModalVariant("edit");
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const allPartiesToSave = [
        ...Array.from(partiesInvolvedEdited.values()),
        ...partiesInvolvedAdded,
      ];
      const partiesInvolved = allPartiesToSave.map((party) => {
        const meta = partyMetaByValue.get(party.partyInvolved);
        const resolvedPartyId = meta?.party_id ?? crypto.randomUUID();
        const resolvedPartyName = meta?.name ?? party.partyInvolved;

        return {
          party_id: resolvedPartyId,
          name: resolvedPartyName,
          party_type: meta?.party_type || null,
          recommended_actions: party.recommendedActions,
          recommended_action_approver: party.approvedBy || null,
          recommended_action_implementation_date:
            party.implementationDate || null,
          implementation_owner: party.implementationOwner || null,
          closure_note: party.closureNote || null,
        };
      });

      await dispatch(
        upsertPartiesInvolvedThunk({
          case_id: props.caseId,
          request: { parties_involved: partiesInvolved },
        })
      ).unwrap();
      await dispatch(fetchPartyOptionsThunk(props.caseId));

      setPartiesInvolvedEdited(new Map());
      setPartiesInvolvedAdded([]);
      setCreatedPartyOptions([]);

      showSuccessToast("Actions saved successfully!");
    } catch (error: any) {
      console.error("Save error:", error);
      showErrorToast("Failed to save actions");
    } finally {
      setIsSaving(false);
      dispatch(resetUpsertStatus());
    }
  };

  const isAddedPartyComplete = (party: IActionsAgainstInvolvedPartyData) => {
    const hasPartyInvolved =
      typeof party.partyInvolved === "string"
        ? party.partyInvolved.trim().length > 0
        : Boolean(party.partyInvolved);

    const hasRecommendedActions =
      Array.isArray(party.recommendedActions) &&
      party.recommendedActions.length > 0;

    return hasPartyInvolved && hasRecommendedActions;
  };

  const hasEditedChanges = partiesInvolvedEdited.size > 0;
  const hasAddedRows = partiesInvolvedAdded.length > 0;

  const hasIncompleteAddedRows =
    hasAddedRows && partiesInvolvedAdded.some((p) => !isAddedPartyComplete(p));

  const hasAnyChanges = hasEditedChanges || hasAddedRows;
  const canSave = hasAnyChanges && !hasIncompleteAddedRows;
  const showSaveHint = hasAddedRows && hasIncompleteAddedRows;

  const isOperationInProgress = isSaving || isDeleting;

  const dataRows = tableData.map((data) => ({
    data,
    isEditable: true,
    widgets: isOperationInProgress ? [] : ["delete", "add"],
    onDelete: handleDeleteRow,
    onAdd: handleAddTableRowClick,
  }));

  const renderDefaultTableBody = () => (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <NoDataIcon />
      <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
        No recommendations yet
      </span>
      <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
        Add recommendations to track actions and share them with the right
        owners
      </span>
      <ButtonV2 onClick={handleAddTableRowClick}>
        <Plus className="w-4 h-4 mr-1" /> Add
      </ButtonV2>
    </div>
  );

  const renderTable = () => (
    <BaseDataTable
      columnConfigs={columnConfigs}
      dataRows={dataRows as IDataRow<IActionsAgainstInvolvedPartyData>[]}
      showDefaultTableBody={tableData.length === 0}
      defaultTableBody={renderDefaultTableBody()}
    />
  );

  const renderClosureNoteModal = () => (
    <ClosureNoteModal
      isOpen={isClosureNoteModalOpen}
      onClose={handleCloseClosureNoteModal}
      variant={modalVariant}
      initialNote={
        selectedRowIndex !== null ? tableData[selectedRowIndex].closureNote : ""
      }
      onSave={handleSaveClosureNote}
    />
  );

  const renderAddPartyModal = () => (
    <AddNewPartyModal
      isOpen={isAddPartyModalOpen}
      onClose={handleCloseAddPartyModal}
      onSave={handleSaveParty}
    />
  );

  const renderDiscardChangesButton = () => {
    const isOperationInProgress = isSaving || isDeleting;

    return (
      <ButtonV2
        variant="outline"
        onClick={handleDiscardChangesClick}
        disabled={isOperationInProgress}
        className={cn(
          "font-sans font-medium text-sm leading-5",
          isOperationInProgress && "opacity-50 cursor-not-allowed"
        )}
        style={{
          width: "137.5px",
          height: "36px",
          borderColor: "#3551F3",
          color: "#3551F3",
          gap: "8px",
        }}
      >
        Discard Changes
      </ButtonV2>
    );
  };

  const renderSaveButton = () => {
    const isOperationInProgress = isSaving || isDeleting;
    const isDisabled = isOperationInProgress || !canSave;

    return (
      <ButtonV2
        variant="primary"
        onClick={handleSave}
        disabled={isDisabled}
        className={cn(
          "font-sans font-medium text-sm leading-5 flex items-center justify-center w-[137.5px] h-[36px] gap-2",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Save className="w-4 h-4 relative right-[4px]" />
        <span className="font-sans font-medium text-sm leading-5 align-middle">
          {isSaving ? "Saving..." : "Save"}
        </span>
      </ButtonV2>
    );
  };

  const renderFooter = () => {
    return (
      <div className="w-full min-h-[44px] flex justify-end items-center gap-5 rounded-lg py-2 px-2.5 border bg-[#F1F5F9] mt-4">
        <div className="flex flex-col items-end flex-1">
          <span className="font-sans font-semibold text-sm leading-5 tracking-normal text-[#737373]">
            {showSaveHint
              ? "Select a party and at least one recommended action to enable Save."
              : "You have made changes, save now"}
          </span>
        </div>
        {renderDiscardChangesButton()}
        {renderSaveButton()}
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderTable()}
      {(partiesInvolvedAdded.length > 0 || partiesInvolvedEdited.size > 0) &&
        renderFooter()}
      {renderClosureNoteModal()}
      {renderAddPartyModal()}
    </div>
  );
};

export default React.memo(ActionsAgainstInvolvedPartiesTable);
